import { useQuery } from "@tanstack/react-query";

import { tenantKeys } from "~api/tenants.ts";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext";
import type { ManagedEmployeesLeisureAndVacationAccountsResponse } from "~generated";
import { TimeAccountsService } from "~generated";
import type { DateRange } from "~lib/dateHelpers.ts";
import type { QueryOpts } from "./helpers";

const MAX_PERIOD_YEARS = 2;

export const leisureAndVacationAccountKeys = {
  base: (tenantId: string) =>
    [
      ...tenantKeys.detail(tenantId),
      "leisureAndVacationAccounts",
      "v1",
    ] as const,
  managedEmployees: (tenantId: string, firstYear: number, lastYear: number) =>
    [
      ...leisureAndVacationAccountKeys.base(tenantId),
      "managedEmployees",
      firstYear,
      lastYear,
    ] as const,
};

export function useLeisureAndVacationAccounts(
  period: DateRange,
  options?: QueryOpts<
    ManagedEmployeesLeisureAndVacationAccountsResponse,
    ReturnType<typeof leisureAndVacationAccountKeys.managedEmployees>
  >,
) {
  const currentTenant = useCurrentTenant();
  const firstYear = period.start.getFullYear();
  const lastYear = period.end.getFullYear();

  const query = useQuery({
    ...options,
    enabled:
      lastYear - firstYear < MAX_PERIOD_YEARS && (options?.enabled ?? true),
    queryKey: leisureAndVacationAccountKeys.managedEmployees(
      currentTenant.id,
      firstYear,
      lastYear,
    ),
    queryFn: () =>
      TimeAccountsService.getManagedEmployeesLeisureAndVacationAccounts(
        currentTenant.id,
        `${firstYear}-01-01`,
        `${lastYear}-12-31`,
      ),
  });

  const timeAccountsByEmployeeId = new Map(
    query.data?.leisure_and_vacation_accounts.map((timeAccount) => [
      timeAccount.employee_id,
      timeAccount,
    ]),
  );

  return { ...query, timeAccountsByEmployeeId };
}
